import { z } from 'zod';
import { SLUG_MAX_LENGTH } from './address';
import { BOARD_TITLE_MAX_LENGTH, boardSchema, boardTitleSchema } from './board';
import { cardSchema, cardTitleSchema } from './card';
import { ulidSchema } from './id';
import { SEARCH_QUERY_MAX_LENGTH, SEARCH_QUERY_MIN_LENGTH } from './search';
import { publicProfileSchema } from './user';

/**
 * Templates — the contract layer for blocks **B36** (board templates) and
 * **B37** (card templates).
 *
 * ⚠ **This file was B36's alone and gained a second half at B37.** The two
 * halves share no schema and no primitive — a board template and a card
 * template are different rows, different requirements and different routes —
 * so a reader could reasonably ask why they share a file. They share it
 * because they share a *vocabulary*: `is_template` on two tables, a
 * mark/unmark pair on each, an instantiation route on each, and one
 * `FR-TMPL-###` document behind all of it. Splitting them into
 * `./board-template` and `./card-template` would put the two answers to "what
 * does this product mean by a template" two files apart, and the second file
 * would open by explaining the first. The module is `./template`, not
 * `./board-template`, precisely so this could happen without a rename.
 *
 * The card half begins at `createCardFromTemplateBodySchema` in the INPUT
 * section and `cardTemplateSchema` in the views; everything before each is
 * B36's and is unchanged by B37, which widened nothing in place here either.
 *
 * ---------------------------------------------------------------------------
 * THE BOARD HALF — B36
 *
 * `BE-36.1` (`FR-TMPL-001` mark a board as a template, `FR-TMPL-007` unmark
 * it), `BE-36.2` (`FR-TMPL-002`, create an independent board from a
 * template) and `BE-36.3` (`FR-TMPL-004`, the template's own description).
 * Five routes, three of them sharing one shape:
 *
 *   | method   | path                              | returns                  |
 *   |----------|-----------------------------------|---------------------------|
 *   | `GET`    | `/boards/:id/template`            | `boardTemplateSchema`     |
 *   | `POST`   | `/boards/:id/template`            | `boardTemplateSchema`     |
 *   | `DELETE` | `/boards/:id/template`            | `boardTemplateSchema`     |
 *   | `PATCH`  | `/boards/:id/template-description`| `boardTemplateSchema`     |
 *   | `POST`   | `/boards/:id/create-from-template`| `boardCopyResultSchema`   |
 *
 * `POST`/`DELETE` on `/template` take no body — the whole gesture is the verb
 * and the path, `POST /boards/:id/close`'s shape (`.b36-design.md` §3.2) —
 * so this file declares no request schema for either. `GET` takes no body
 * either. That leaves exactly two request shapes and one response shape of
 * this file's own; the fifth route's response is **not** a fourth shape
 * declared here — see the note at the foot of this header before reaching
 * for one.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `boardSchema` IS EXTENDED ON A COPY, NEVER WIDENED IN PLACE
 * ---------------------------------------------------------------------------
 * `boardSchema` (`./board`) serialises `GET /boards/:id`, both board lists,
 * `POST /boards/:id/copy` and now `POST /boards/:id/create-from-template`
 * (via `boardCopyResultSchema`, imported there, not here). `boardAddressSchema`
 * in that same file is the precedent for exactly this shape of extension —
 * `boardSchema.extend({ path })` for the short-link resolver — and its own
 * docstring gives the second half of the reason to extend rather than nest: a
 * caller should be able to hand `boardTemplateSchema`'s result to anything
 * that takes a board, the way a resolver's caller can already hand it
 * `boardAddressSchema`'s. Widening `boardSchema` itself would put `isTemplate`
 * on every board-list row and every plain `GET /boards/:id`, none of which
 * asked for it — `CLAUDE.md`'s "extend a shipped schema on a copy" rule, and
 * `./board`'s own "an input primitive reused as a response field is the
 * mistake the division prevents" restated one level up, for a whole schema
 * rather than one field.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `title` IS REQUIRED ON `createBoardFromTemplateBodySchema`, UNLIKE
 * `boardCopyBodySchema`'S OPTIONAL ONE — READ `./copy.ts` BEFORE ASSUMING
 * THE TWO BODIES SHOULD MATCH
 * ---------------------------------------------------------------------------
 * `boardCopyBodySchema` (`./copy`, B35) makes `title` optional: omitting it
 * means "inherit the source's title, suffixed on a collision" —
 * `nextCopyName` (`apps/server/src/copy/duplicate-name.ts`) computes the
 * fallback. `createBoardFromTemplateBodySchema` below has no such fallback
 * and cannot borrow that one, because `FR-TMPL-002` AC1 is explicit that the
 * creation dialog "asks for a title and a destination workspace before the
 * board is created" — the requirement's own words describe a form the user
 * fills in, not a default the server may silently supply. `.b36-design.md`
 * §3.6 states the same thing from the implementation side: `copyBoardInto`
 * (the shared engine both routes call) "takes a resolved `title: string` and
 * does no naming of its own" — so a caller of *this* route that omitted the
 * title would have nothing for the engine to resolve, where `POST
 * /boards/:id/copy`'s caller has `nextCopyName` standing behind it. Two
 * routes sharing one orchestration function is not two routes sharing one
 * request contract, and this field is where that shows up on the wire.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE FIFTH ROUTE'S RESPONSE IS NOT DECLARED HERE — `boardCopyResultSchema`
 * (`./copy`) IS REUSED WHOLE, AND THE REASON IS THE OPPOSITE OF
 * `cardCopyResultSchema`'S OWN ARGUMENT FOR *NOT* REUSING SOMETHING
 * ---------------------------------------------------------------------------
 * `./copy.ts`'s header states its house rule for two routes that merely
 * *look* alike: "two routes with the same shape today is not two routes with
 * the same contract" — the reason `cardCopyResultSchema` and
 * `cardMoveToBoardResultSchema` are two declarations rather than one, even
 * though they are field-identical. That rule does not fire here, for the
 * reason `./copy.ts` itself names for `reconciliationSchema`'s reuse: this is
 * the case where the underlying mechanism is *actually* shared, not
 * coincidentally matching. `.b36-design.md` §3.6 is explicit that
 * `POST /boards/:id/create-from-template` calls the identical
 * `copyBoardInto` (`apps/server/src/copy/copy-board.ts`) that
 * `POST /boards/:id/copy` calls, with a `fromTemplateBoardId` argument
 * deciding only which key `board.created`'s payload carries and which column
 * the new board's `created_from_template_board_id` is populated from — the
 * response the two callers produce is `{ board, cardsCopied }` **by
 * construction**, not by two independent handlers agreeing today and free to
 * drift tomorrow. Declaring a second `{ board, cardsCopied }` shape here
 * would be exactly the mistake `./copy.ts`'s own reconciliation note warns
 * against in the other direction: two names for one contract, with nothing
 * to stop them diverging the day either route's response needs a field the
 * other does not. So this file imports nothing from `./copy` — there is
 * nothing here that references it — and the route file
 * (`apps/server/src/routes/board-templates.ts`) imports
 * `boardCopyResultSchema` directly for `POST /boards/:id/create-from-template`'s
 * response. `packages/shared/src/index.ts`'s barrel entry for this module
 * records the same thing for whoever next reads the export list wondering
 * why a five-route file only declares three shapes.
 *
 * ---------------------------------------------------------------------------
 * THE CARD HALF — B37
 * ---------------------------------------------------------------------------
 * `BE-37.1` (`FR-TMPL-008` mark a card as a template, `FR-TMPL-012` turn one
 * back into a normal card), `BE-37.2` (`FR-TMPL-009`/`FR-TMPL-010`, create a
 * card from a template) and `BE-37.3` (the board's own picker listing).
 * `FR-CARD-031` is the card back's entry point to the same capability rather
 * than a second one, so it adds no shape here.
 *
 *   | method   | path                             | returns                  |
 *   |----------|----------------------------------|---------------------------|
 *   | `GET`    | `/cards/:id/template`            | `cardTemplateSchema`      |
 *   | `POST`   | `/cards/:id/template`            | `cardTemplateSchema`      |
 *   | `DELETE` | `/cards/:id/template`            | `cardTemplateSchema`      |
 *   | `POST`   | `/cards/:id/create-from-template`| `cardCopyResultSchema`    |
 *   | `GET`    | `/boards/:id/card-templates`     | `cardTemplateListSchema`  |
 *
 * The board half's shape, one container down and with one difference worth
 * naming before a reader assumes symmetry: **there is no card-level
 * `template-description`.** `FR-TMPL-004` gives a *board* template an "about
 * this template" description because a board template is a thing somebody
 * browses in a gallery; a card template is picked from a list of that board's
 * own cards by somebody who is already on the board, and `FR-TMPL-008` /
 * `FR-TMPL-009` ask for no such field. `cards` carries no
 * `template_description` column either, so the absence is the schema's as
 * well as this file's.
 *
 * ⚠ **The fourth route's response is `cardCopyResultSchema` (`./copy`),
 * reused whole and NOT declared here** — the identical call the board half
 * made for `boardCopyResultSchema`, for the identical reason and not by
 * analogy: `POST /cards/:id/create-from-template` calls the same
 * `copyCardInto` engine (`apps/server/src/copy/copy-card.ts`) that
 * `POST /cards/:id/copy` calls, with one argument selecting the event name,
 * so `{ card, reconciliation }` is the response **by construction** rather
 * than by two handlers agreeing today. This file therefore imports nothing
 * from `./copy`; the route file imports that schema directly.
 *
 * ⚠ **Consequence a reader of that response must not mistake for a bug**, and
 * it is stated here because this is where the shape is named:
 * `reconciliation` comes back with three EMPTY label groups and no dropped
 * members on **every** request this route can serve. `FR-TMPL-008` scopes
 * template cards to their own board and `FR-TMPL-009` puts the composer entry
 * "at the bottom of any list on the same board", so the destination is always
 * the template's own board, `copyCardInto`'s `crossBoard` predicate is always
 * `false`, and `FR-TMPL-019`'s label reconciliation and `FR-TMPL-020`'s member
 * drop are structurally unreachable here. Empty is correct, not missing.
 *
 * ---------------------------------------------------------------------------
 * WHAT ELSE THIS FILE DELIBERATELY DOES NOT DECLARE
 * ---------------------------------------------------------------------------
 * No `PATCH`/`POST`/`DELETE` params schema for the path's `:id` — every
 * route file in this product validates that with `ulidSchema` at the route
 * boundary, not with a body schema, and this block introduces nothing new
 * there. No `templateCategory` or `templateCopyCount` field on
 * `boardTemplateSchema` — `.b36-design.md` §3.5 is explicit that neither
 * column is written by any route in this block (`template_category` is
 * `FR-TMPL-005`'s, `template_copy_count` is derivable from
 * `activity_entries` and is not maintained as a second, drift-prone
 * counter), and a field on the wire is a field a later block may not
 * quietly remove — the same "deliberately absent" discipline `./board`'s own
 * header keeps for `description`, the background columns and the rest of
 * `boards`' still-unshipped surface.
 */

// ---------------------------------------------------------------------------
// Column width.
// ---------------------------------------------------------------------------

/**
 * `boards.template_description text NULL` — `FR-TMPL-004`.
 *
 * ⚠ **This bound describes a `text` column, not a `varchar`, and that
 * distinction is load-bearing rather than pedantic — `./card-detail.ts`'s
 * `CARD_DESCRIPTION_MAX_LENGTH` makes the identical point for
 * `cards.description` and the argument transfers unchanged.** Unlike
 * `BOARD_TITLE_MAX_LENGTH` (`./board`, describing `boards.title
 * varchar(512)`), exceeding this number is **not** a
 * `value too long for type character varying(n)` at INSERT time: Postgres
 * enforces nothing on a `text` column, so this constant — enforced only at
 * this schema's boundary — is the *entire* bound there is. If it were
 * removed from `templateDescriptionPatchSchema` below, nothing in the
 * database would refuse an arbitrarily long description; the "same posture
 * `boardTitleSchema` takes against `varchar(512)`" that `.b36-design.md` §3.9
 * invokes is the posture of *declaring a width constant next to the column
 * it bounds*, not a claim that a CHECK constraint stands behind this one too.
 *
 * **16384, matching `CARD_DESCRIPTION_MAX_LENGTH` in value, is not shared
 * with it as a constant.** `./card`'s rule, restated at every column-width
 * constant in this package: *import the constant describing your column if
 * one exists, declare it if none does, and never share one across two
 * columns.* `boards.template_description` and `cards.description` are two
 * separate columns behind two separate requirements — nothing stops
 * `FR-TMPL-004` from changing this number independently of `FR-CARD-003` —
 * so the coincidence in magnitude (both are 2^14, the product's standing
 * convention for "a long-form free-text field") is exactly that: a
 * coincidence, recorded here so a future reader does not "simplify" the two
 * into one import and couple two requirements that happen to agree today.
 *
 * The **view** field below (`boardTemplateSchema.templateDescription`) is
 * bounded by this same number, unlike `cardDetailSchema.description`'s
 * deliberately *unbounded* read — see that field's own docstring for why a
 * card's description view refuses to enforce a product cap the database
 * does not. The difference is not an inconsistency: `cards.description`
 * predates its own product cap, so the read path has to serve rows a client
 * saved before `FR-CARD-003` existed. `boards.template_description` has no
 * such history — the only writer that has ever existed for it is
 * `templateDescriptionPatchSchema` below, which enforces this bound on every
 * write from the column's first day — so there is no legacy row the read
 * path needs to tolerate, and bounding the view costs nothing a real row
 * could ever trip.
 */
export const TEMPLATE_DESCRIPTION_MAX_LENGTH = 16384;

export const TEMPLATE_CATEGORIES = [
  'business',
  'design',
  'education',
  'engineering',
  'marketing',
  'personal',
  'product_management',
  'project_management',
  'remote_work',
  'sales',
  'support',
] as const;

export const templateCategorySchema = z.enum(TEMPLATE_CATEGORIES);

// ---------------------------------------------------------------------------
// INPUT primitives — what a route ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * `PATCH /boards/:id/template-description` — `FR-TMPL-004` AC1: "I can write
 * and save a description that is distinct from the board's own description."
 * SC-4, field-scoped, `boardTitlePatchSchema`'s shape (`./board`): one field,
 * named after what it patches, on a path that already scopes the subject —
 * the body says `description`, not `templateDescription`, for the same
 * reason `boardTitlePatchSchema`'s body says `title` on `/boards/:id/title`
 * rather than `boardTitle`: the path segment already carries the scope, so
 * the body does not restate it.
 *
 * ---------------------------------------------------------------------------
 * ⚠ TRIM, THEN COLLAPSE EMPTY TO `null` — DELIBERATELY THE OPPOSITE OF
 * `cardDescriptionPatchSchema`'S (`./card-detail`) POSTURE, FOR A REASON
 * SPECIFIC TO THIS COLUMN
 * ---------------------------------------------------------------------------
 * `cardDescriptionPatchSchema` does **not** trim, because a card description
 * is Markdown and Markdown is whitespace-sensitive — that file's own header
 * gives the full argument (leading spaces inside a fenced code block are
 * content, two trailing spaces are a hard line break). A template's "about
 * this template" description carries no such requirement: `FR-TMPL-004`
 * never calls it Markdown, never mentions rendering, and describes it only
 * as prose shown "alongside the creator's name" on a preview — the same
 * footing `bioSchema` (`./user`) already treats a short free-text field on.
 * So this primitive trims, exactly as `boardTitleSchema` and `bioSchema` do,
 * and the collapse below is safe to perform *in the schema* rather than
 * deferred to the route the way `cardDescriptionPatchSchema`'s header
 * explains it must be for Markdown.
 *
 * **`null` clears it; so does `''`; so does whitespace-only text — one
 * state, one representation**, `bioPatchSchema`'s rule (`./user`: "Clearing a
 * bio is `null`, never `''`") extended one step further than that file needed
 * to go, because there the trim already made `''` and `'   '` the same
 * request and the schema could simply *refuse* `''` and demand `null`. Here
 * the brief is explicit that **both spellings must be accepted and
 * normalised to the same output**, not one accepted and the other refused:
 * `.b36-design.md` §3.9 states it as "Trim, then treat `''` as `null`: an
 * empty description and no description are the same state and storing both
 * would give the read surface two ways to say one thing" — a caller sending
 * an explicit `null` (a client's "clear" button) and a caller sending `''`
 * (a client that trims its own textarea to nothing) are asking for the
 * identical thing and neither is refused.
 *
 * The chain matters in the order it is written: `.trim()` before `.max()`
 * (`boardTitleSchema`'s own ordering — trim first, so a value that is only
 * over the bound *before* trimming still parses) before `.transform()`
 * (empty-after-trim becomes `null`) before `.nullable()` (so a literal
 * `null` short-circuits the whole chain and is never run through `.trim()`
 * or the transform at all). `z.infer` on this schema is the **output**
 * side — always `string | null`, the empty string never survives — which is
 * the side the house rule ("declare a schema, derive the type with
 * `z.infer`") means; the schema's *input* type is wider (`string | null`,
 * where an input string may be `''` or whitespace) and that divergence is
 * exactly what a `.transform()` is for.
 */
export const templateDescriptionPatchSchema = z.strictObject({
  description: z
    .string()
    .trim()
    .max(TEMPLATE_DESCRIPTION_MAX_LENGTH)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .describe(
      'The template’s “about this template” description, distinct from the ' +
        'board’s own description (FR-TMPL-004 AC1), up to 16,384 characters. ' +
        'Send null, an empty string or whitespace-only text to clear it — all ' +
        'three normalise to the same stored state, so “no description” has ' +
        'exactly one representation.',
    ),
});

export type TemplateDescriptionPatch = z.infer<typeof templateDescriptionPatchSchema>;

export const TEMPLATE_VISIBILITIES = ['private', 'workspace_visible', 'public'] as const;

/**
 * `PATCH /boards/:id/template-visibility` — `FR-TMPL-003`, `BE-65.1`.
 *
 * ⚠ **A discriminated union, not `{ visibility: z.enum(...) }`, for the same
 * reason `boardVisibilityPatchSchema` (`./board`) is one: this route writes
 * the identical `boards.visibility` column, so it owes that route's identical
 * `confirmWorldReadable` interlock (`FR-PERM-010`) rather than a
 * template-specific approximation of it.** `BE-65.1`'s own audit
 * (`docs/test-reports/BE-65.1.md`, F1) found the plain-enum shape let a board
 * admin reach `public` — and therefore genuinely world-readable, since
 * `GET /boards/:id` serves any public board to an anonymous caller — through
 * this route with no acknowledgement at all, while the sibling route refused
 * the identical transition without one. The fix is the same shape as the
 * sibling schema, narrowed to the three visibilities this route accepts:
 * `enterprise_visible` stays refused until `BE-67.1`'s read arm is wired into
 * this facet, exactly as before, and now via "not a member of this union"
 * (422) rather than "not a member of this enum" (422) — the response code and
 * shape a client already handles are unchanged.
 */
export const templateVisibilityPatchSchema = z.discriminatedUnion('visibility', [
  z
    .strictObject({ visibility: z.literal('private') })
    .describe(
      'Private — only board members can find or open this template. ' + 'FR-TMPL-003.',
    ),
  z
    .strictObject({ visibility: z.literal('workspace_visible') })
    .describe(
      'Workspace-visible — every member of the template’s workspace can open ' +
        'it, badged in the workspace boards list. FR-TMPL-003.',
    ),
  z
    .strictObject({
      visibility: z.literal('public'),
      confirmWorldReadable: z
        .literal(true, {
          error:
            'confirmWorldReadable must be true to make a template public: it ' +
            'becomes readable by anyone on the internet, signed in or not.',
        })
        .describe(
          'Required acknowledgement that the template board becomes ' +
            'world-readable, identical to boardVisibilityPatchSchema’s field ' +
            'of the same name (FR-PERM-010). Send true to confirm; the ' +
            'request is refused without it.',
        ),
    })
    .describe(
      'Public — readable by anyone on the internet, signed in or not. ' +
        'Requires confirmWorldReadable: true. FR-TMPL-003.',
    ),
]);

export type TemplateVisibilityPatch = z.infer<typeof templateVisibilityPatchSchema>;

export const templateCategoryPatchSchema = z.strictObject({
  category: templateCategorySchema
    .nullable()
    .describe('The public-gallery category for a board template, or null to clear it.'),
});

export type TemplateCategoryPatch = z.infer<typeof templateCategoryPatchSchema>;

/**
 * `POST /boards/:id/create-from-template` — `FR-TMPL-002`.
 *
 * `{ workspaceId, visibility, title, keepCards }` — see the module header
 * for why `title` is required here and optional on `boardCopyBodySchema`
 * (`./copy`), the two fields this body shares a name with but not a
 * treatment of.
 */
export const createBoardFromTemplateBodySchema = z.strictObject({
  workspaceId: ulidSchema.describe(
    'The destination workspace — FR-TMPL-002 AC1: the creation dialog ' +
      '"asks for a title and lets the member pick the destination workspace ' +
      'and visibility." `boards.workspace_id` is NOT NULL, so there is no ' +
      'board-less branch, on createBoardBodySchema’s and boardCopyBodySchema’s ' +
      'own footing.',
  ),
  /**
   * ⚠ **A three-member enum restated inline, not imported from
   * `boardCopyBodySchema.shape.visibility`.** `./copy.ts` exports no named
   * const for its own visibility field — it is inline there too — so there
   * is nothing to import, and reaching for `boardCopyBodySchema.shape.visibility`
   * would couple this body's type to a sibling schema's internal shape rather
   * than to a vocabulary either can restate on its own terms
   * (`cross-board-move.ts`'s "two routes with the same shape today is not
   * two routes with the same contract", the same rule the module header
   * invokes in the other direction for the response). `enterprise_visible`
   * is refused for the identical reason `boardCopyBodySchema`'s docstring
   * gives: it has no read arm anywhere in the product yet (`BE-67.1`), and
   * admitting it here would store a value nothing can read back.
   */
  visibility: z
    .enum(['private', 'workspace_visible', 'public'])
    .describe(
      'The new board’s visibility, chosen at creation rather than inherited ' +
        'from the template silently. `enterprise_visible` is refused — no ' +
        'read arm exists yet (BE-67.1) — on boardCopyBodySchema’s identical ' +
        'stance.',
    ),
  /**
   * ⚠ **Required, not `.optional()` — the divergence from
   * `boardCopyBodySchema.title` argued at length in the module header.**
   * `boardTitleSchema` itself (`./board`) already trims and enforces
   * `.min(1)`, so an empty or whitespace-only title is refused by the
   * imported primitive; what changes here is only that the *key* must be
   * present at all.
   */
  title: boardTitleSchema.describe(
    'The new board’s title — REQUIRED (FR-TMPL-002 AC1), unlike ' +
      'boardCopyBodySchema’s optional one: there is no nextCopyName ' +
      'fallback on this route, because copyBoardInto (apps/server/src/' +
      'copy/copy-board.ts) takes a resolved title and does no naming of ' +
      'its own. Omitting this key is a 422, not a suffix.',
  ),
  keepCards: z
    .boolean()
    .default(true)
    .describe(
      'Whether the template’s cards travel at all, as title + description ' +
        'only — never checklists, labels, members, attachments or dates ' +
        '(the same tier boardCopyBodySchema.keepCards documents in full). ' +
        'Clearing this keeps the new board’s lists and label set but zero ' +
        'cards (FR-TMPL-002 AC2); cardsCopied: 0 in the response is what ' +
        'makes that acceptance criterion assertable without a second read. ' +
        'Defaults to true, matching boardCopyBodySchema’s own default and ' +
        'FR-TMPL-002’s own "given I CLEAR the keep-cards choice" phrasing.',
    ),
});

export type CreateBoardFromTemplateBody = z.infer<
  typeof createBoardFromTemplateBodySchema
>;

/**
 * `POST /cards/:id/create-from-template` — `FR-TMPL-009`, `FR-TMPL-010`, and
 * the action `FR-CARD-031` puts on the card back.
 *
 * `{ listId, title, previousCardId?, nextCardId? }` — the template card is
 * the path, the destination is the body, and there is deliberately nothing
 * else in it. Compare the three bodies this one sits between:
 * `createBoardFromTemplateBodySchema` above takes a workspace and a
 * visibility because a board is created into a *container the caller chooses*
 * and carries its own permission surface; `cardCopyBodySchema` (`./copy`)
 * takes five `keep*` toggles because `FR-TMPL-013`'s dialog offers them. This
 * body takes neither.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ NO `keep*` TOGGLES, AND THAT IS THE REQUIREMENT RATHER THAN A NARROWING
 * ---------------------------------------------------------------------------
 * `FR-TMPL-010` states the carry-over set as a fact about the product —
 * a card created from a template "carries the template's description,
 * checklists (including all items), labels, members, attachments, cover …
 * It does not carry start or due dates" — with no dialog, no choice and no
 * acceptance criterion about choosing. `FR-TMPL-013`'s copy dialog is where
 * the choices live, and a template instantiation is not a copy dialog: it is
 * a composer with a title in it (`FR-TMPL-009`: "the member supplies a
 * title"). So the tier is a **server-side constant**
 * (`CARD_TEMPLATE_ELEMENTS` in `apps/server/src/copy/copy-card.ts`, beside
 * that engine's other tiers) and not five booleans on the wire. A body that
 * accepted them would let a client ask for a card that `FR-TMPL-010` says
 * cannot exist — one carrying the template's due date — and there would be no
 * requirement to point at when deciding what to answer.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `title` IS REQUIRED, USED EXACTLY, AND NEVER SUFFIXED
 * ---------------------------------------------------------------------------
 * The same divergence `createBoardFromTemplateBodySchema.title` argues
 * against `boardCopyBodySchema`'s optional one, one container down and with a
 * sharper edge, because here the fallback does not merely go unused — **it
 * does not exist**. `nextCopyName` (`apps/server/src/copy/duplicate-name.ts`)
 * is what makes `cardCopyBodySchema.title` safely optional; it has a list
 * branch and a board branch and **no card branch**, because `FR-TMPL-016`
 * ("Copy N", the duplicate-name rule) names only lists and boards. A caller
 * omitting this key would therefore leave the engine with nothing to resolve.
 *
 * ⚠ **And a card created from a template must not be suffixed even if a
 * suffixer existed**: it is not a duplicate of the template, it is a new card
 * whose name the member typed. "Onboarding checklist (2)" would be a
 * plausible, silently wrong title on every card this route creates.
 * `cardTitleSchema` (`./card`) already trims and enforces `.min(1)`, so an
 * empty or whitespace-only title is refused by the imported primitive; what
 * this field adds is only that the key must be present at all — omitting it
 * is a 422, not a default.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `listId` NAMES A LIST ON THE TEMPLATE'S OWN BOARD, AND THIS SCHEMA
 * CANNOT SAY SO
 * ---------------------------------------------------------------------------
 * `FR-TMPL-008`: "Template cards are scoped to the board they live on and are
 * not shared with other boards." `FR-TMPL-009` puts the composer entry "at
 * the bottom of any list on the **same board**". A Zod schema cannot express
 * "on the same board as the card in the path" — `cardMoveBodySchema`
 * (`./card`) makes exactly this point for `FR-BOARD-025` — so what this body
 * does instead is refuse to carry a `boardId` at all, which makes a
 * cross-board instantiation **unaskable** rather than merely refused. The
 * route resolves the destination list through its own guard and answers
 * **404** for a list on another board: a caller must not learn that a list
 * exists on a board they were not addressing.
 */
export const createCardFromTemplateBodySchema = z.strictObject({
  listId: ulidSchema.describe(
    'The destination list — FR-TMPL-009’s "any list on the same board". A ' +
      'list on ANOTHER board is a 404, resolved through that list’s own ' +
      'guard rather than refused here, and no boardId is accepted: the ' +
      'card’s board is read from this list.',
  ),
  title: cardTitleSchema.describe(
    'The new card’s title — REQUIRED (FR-TMPL-009: "the member supplies a ' +
      'title"), used exactly as typed and NEVER suffixed. Unlike ' +
      'cardCopyBodySchema’s optional title there is no fallback to fall ' +
      'back to: nextCopyName has a list branch and a board branch and no ' +
      'card branch. Trimmed; a whitespace-only submission is refused.',
  ),
  /**
   * ⚠ **Both neighbours default to `null`, and that pair means THE BOTTOM OF
   * THE DESTINATION LIST** — `createCardBodySchema`'s and
   * `cardMoveBodySchema`'s reading in `./card`, inherited verbatim rather
   * than re-decided, because a third meaning for one pair of nulls in one
   * package is how a card silently lands at the top of a list nobody asked
   * it to.
   *
   * ⚠ **The default is a DECISION here rather than an inherited fallback.**
   * `FR-TMPL-009` has the member pick a *list*, never a position — its own
   * words are "a new non-template card with that title appears in the chosen
   * list" — and the surface it describes is the add-card composer, which
   * appends. The two fields exist at all because the same capability is also
   * reachable from the card back (`FR-CARD-031`) and because refusing a
   * client the ability to name a drop position would make this the one
   * card-creating route in the product that cannot, for no requirement's
   * sake.
   *
   * SC-2: there is no `position` field and no client may compute a rank. The
   * route hands this pair to the same `resolveGap` helpers
   * `POST /cards/:id/copy` uses, whose both-null branch is `lastRankInList`
   * then `rankInGap`.
   */
  previousCardId: ulidSchema
    .nullable()
    .default(null)
    .describe(
      'The card immediately ABOVE the gap the new card goes into, in the ' +
        'destination list. Omit both neighbours for the bottom of that list, ' +
        'which is what FR-TMPL-009’s composer does.',
    ),
  nextCardId: ulidSchema
    .nullable()
    .default(null)
    .describe(
      'The card immediately BELOW the gap the new card goes into. See ' +
        'previousCardId; omitting both is legal and means the bottom of the ' +
        'destination list.',
    ),
});

export type CreateCardFromTemplateBody = z.infer<typeof createCardFromTemplateBodySchema>;

// ---------------------------------------------------------------------------
// Views — what a route RETURNS.
//
// Width-bounded strings, no format checks, no transforms — `./board`'s own
// rule for this half of the file, restated once more: a view that trimmed or
// refused a legal row would be a 500 on a read for data the database already
// holds.
// ---------------------------------------------------------------------------

/**
 * What `GET`, `POST` and `DELETE /boards/:id/template`, and
 * `PATCH /boards/:id/template-description`, all answer with —
 * `FR-TMPL-001`, `FR-TMPL-004`, `FR-TMPL-007`.
 *
 * `boardSchema.extend(...)`, on `boardAddressSchema`'s precedent in the same
 * file — see the module header for the full argument against widening
 * `boardSchema` in place.
 *
 * **Answers for any readable board, not only for a template.**
 * `.b36-design.md` §3.9: "`GET /boards/:id/template` answers for any
 * readable board … `isTemplate: false` for an ordinary one." A 404 on a
 * non-template board would make `FR-TMPL-001` AC3's badge unaskable, so
 * `requireBoardReader` is the only guard on the `GET`.
 *
 * ⚠⚠ **UPDATE (`BE-65.1`, commit `3a0d778`): A SIGNED-OUT CALLER CAN NOW REACH
 * THIS ROUTE FOR A PUBLIC TEMPLATE, BUT STILL GETS `404` — NOT `200`, NOT
 * `401` — FOR AN ORDINARY PUBLIC BOARD.** This paragraph used to claim the
 * shipped route required a session unconditionally, as a recorded gap against
 * `.b36-design.md` §3.9 and `FR-TMPL-003` AC4 ("given a public template, when
 * a signed-out visitor opens its URL, then they can view it"); `BE-65.1`
 * closed that gap. The server-side guard
 * (`assertBoardTemplateOverviewReadable` in
 * `apps/server/src/routes/board-templates.ts`) adds `boards.isTemplate = true`
 * to the predicate only when `userId === null`, so an anonymous caller gets
 * `200` for a public board that **is** a template and `404` for one that is
 * not — a failed predicate is indistinguishable from a missing row, same as
 * every other permission module in this product. `GET /boards/:id` still
 * serves an ordinary public board anonymously; that is a different, wider
 * scope and the asymmetry is intentional, not a contradiction.
 *
 * `apps/web/e2e/FR-TMPL-001.spec.ts` (owned by `BE-36.1`) pinned the old
 * unconditional `401` and was stale until fixed alongside this docstring; it
 * now asserts `404` for the anonymous non-template case.
 * `board-templates.integration.test.ts` already asserted the shipped `200`/
 * `404` split correctly and needed no change.
 *
 * ---------------------------------------------------------------------------
 * `creator` — RESOLVED, NOT STORED, AND GENUINELY NULLABLE
 * ---------------------------------------------------------------------------
 * `FR-TMPL-004` AC2: "the description is shown alongside the creator's
 * name." `boards` carries no `created_by` column, so the server resolves
 * this from the board's own `board.created` activity entry (`actor_user_id`,
 * `ORDER BY created_at, id LIMIT 1`) — a fact this schema's docstring records
 * because a reader reaching for `boards.created_by` in the route file will
 * not find it.
 *
 * The `null` is **real, not defensive**: an entry can be absent for a board
 * old enough to predate `0011`–`0013`'s event work, and `.b36-design.md`
 * §3.9 names the requirement this satisfies directly — "templates with no
 * description must still be copyable" (`FR-TMPL-004`'s edge case) "is a
 * preview with no author name one field over." A schema that refused to
 * serve `creator: null` would make that board's template overview a 500
 * rather than a preview with a gap in it.
 */
export const boardTemplateSchema = boardSchema.extend({
  /** `boards.is_template`, `NOT NULL DEFAULT false`. */
  isTemplate: z.boolean(),
  /**
   * `boards.template_description` — bounded, unlike
   * `cardDetailSchema.description`; see `TEMPLATE_DESCRIPTION_MAX_LENGTH`'s
   * own docstring for why that divergence is deliberate rather than an
   * inconsistency between the two "long description" fields in this
   * package.
   */
  templateDescription: z.string().max(TEMPLATE_DESCRIPTION_MAX_LENGTH).nullable(),
  /** `boards.template_category`, used by the public gallery and nullable for uncategorised templates. */
  templateCategory: templateCategorySchema.nullable(),
  /** Derived from boards.created_from_template_board_id; never from boards.template_copy_count. */
  copyCount: z.number().int().nonnegative(),
  /** See the field's own note above the schema. */
  creator: publicProfileSchema.nullable(),
});

export type BoardTemplate = z.infer<typeof boardTemplateSchema>;

export const templateGalleryQuerySchema = z.strictObject({
  category: templateCategorySchema.optional(),
  q: z
    .string()
    .trim()
    .min(SEARCH_QUERY_MIN_LENGTH)
    .max(SEARCH_QUERY_MAX_LENGTH)
    .optional(),
});

export type TemplateGalleryQuery = z.infer<typeof templateGalleryQuerySchema>;

export const templateGalleryItemSchema = z.object({
  id: ulidSchema,
  workspaceId: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  slug: z.string().max(SLUG_MAX_LENGTH),
  shortLink: boardSchema.shape.shortLink,
  path: z.string().min(1),
  templateDescription: z.string().max(TEMPLATE_DESCRIPTION_MAX_LENGTH).nullable(),
  templateCategory: templateCategorySchema.nullable(),
  creator: publicProfileSchema.nullable(),
  copyCount: z.number().int().nonnegative(),
});

export type TemplateGalleryItem = z.infer<typeof templateGalleryItemSchema>;

export const templateGallerySchema = z.strictObject({
  templates: z.array(templateGalleryItemSchema),
});

export type TemplateGallery = z.infer<typeof templateGallerySchema>;

/**
 * What `GET`, `POST` and `DELETE /cards/:id/template` all answer with, and
 * the element type of the picker listing below — `FR-TMPL-008`,
 * `FR-TMPL-012`.
 *
 * ---------------------------------------------------------------------------
 * ⚠ `cardSchema` IS EXTENDED ON A COPY, NEVER WIDENED IN PLACE
 * ---------------------------------------------------------------------------
 * `boardTemplateSchema` above is the precedent and the argument is the same
 * one, with more at stake: `cardSchema` (`./card`) serialises `POST /cards`,
 * `GET /lists/:id/cards`, `PATCH /cards/:id/title` and `POST /cards/:id/move`,
 * and is extended on a copy twice already (`cardDetailSchema` in
 * `./card-detail`, `boardFaceCardSchema` in `./board-contents`). Widening it
 * would put `isTemplate` on every card of every list read and on the drag
 * path — responses that never asked for it — which is `CLAUDE.md`'s "extend a
 * shipped schema on a copy" rule at the one place in this package where the
 * response scales with the size of the board.
 *
 * ⚠ **So `isTemplate` is ABSENT from the board face, and that is a recorded
 * gap rather than an oversight.** `FR-TMPL-008` AC1's badge ("the card shows
 * a template badge on the board") is a board-face read, and `boardFaceCardSchema`
 * does not carry the flag. Closing it is a copy-extension in
 * `./board-contents`, by whoever builds the badge; it is not this file's to
 * do by widening, and it is named in `apps/server/README.md` rather than left
 * for a client to discover.
 *
 * ---------------------------------------------------------------------------
 * `cardSchema` AND NOT `cardDetailSchema` — THE ONE CHOICE IN THIS SHAPE
 * ---------------------------------------------------------------------------
 * `cardDetailSchema` is the obvious base for a route whose path is a card,
 * and it is the wrong one twice over.
 *
 *   1. **This shape is a listing element as well as a single read.** The
 *      picker (`cardTemplateListSchema`, below) returns one of these per
 *      template card on the board, and `cardDetailSchema` carries the card's
 *      full description (up to 16 KB), its members, its labels, its dates and
 *      its cover. `./card-detail`'s own header states the rule this obeys —
 *      "the card back is opened one card at a time; the board face is opened
 *      with two hundred" — and a picker is the board face's shape, not the
 *      card back's.
 *   2. **The three single-card routes are answering "what is this card now",
 *      which is `cardSchema`'s own stated question.** `POST` and `DELETE`
 *      here flip one boolean on `cards`; answering with the whole card back
 *      would make a template toggle read a card's members, labels and
 *      checklists to say what changed about none of them.
 *
 * The consequence for a client is small and worth stating: a card back that
 * has just toggled the flag holds a `cardTemplateSchema`, not a
 * `CardDetail`, so it updates its badge from this response and keeps the
 * detail it already had. Nothing in this response contradicts it —
 * `cardSchema`'s eleven fields are a subset of `cardDetailSchema`'s and are
 * read from the same row.
 *
 * ---------------------------------------------------------------------------
 * ⚠ IT ANSWERS FOR ANY READABLE CARD, TEMPLATE OR NOT
 * ---------------------------------------------------------------------------
 * `isTemplate: false` is a legal, ordinary answer, exactly as
 * `boardTemplateSchema.isTemplate` is: `FR-TMPL-008` AC1 is a badge read, and
 * a 404 on a non-template card would make the badge unaskable. That is what
 * makes this field the point of the shape rather than a constant — see
 * `cardTemplateListSchema` below, where it is a constant and is kept anyway,
 * with the trade stated.
 *
 * ⚠ **And unlike its board-level sibling, `GET /cards/:id/template` is
 * ANONYMOUS-CAPABLE.** `boardTemplateSchema`'s docstring records B36's gap —
 * a signed-out caller gets `401` there even on a public board, because
 * `requireBoardReader` takes a non-nullable user id by design.
 * `requireCardReader` takes `string | null` and already backs the
 * anonymous-capable `GET /cards/:id`, so the card side costs nothing and is
 * simply built. B36's board-side gap is untouched and stays open.
 *
 * There is no `creator` field here, unlike `boardTemplateSchema`.
 * `FR-TMPL-004` AC2 asks for a board template's description to be shown
 * "alongside the creator's name"; no requirement asks that of a card
 * template, there is no card-level template description for it to sit
 * beside, and resolving one would mean a `card.created` lookup per row of the
 * picker.
 */
export const cardTemplateSchema = cardSchema.extend({
  /**
   * `cards.is_template`, `NOT NULL DEFAULT false` — a column that has existed
   * since `0007_templates_reuse_and_recovery.sql` and that no route wrote
   * before `FR-TMPL-008`.
   */
  isTemplate: z.boolean(),
  /**
   * `cards.hidden_from_list`, `NOT NULL DEFAULT false` — exposed only on the
   * card-template facet. Ordinary card reads still do not carry this display
   * lifecycle flag.
   */
  hiddenFromList: z.boolean(),
});

export type CardTemplate = z.infer<typeof cardTemplateSchema>;

/**
 * What `GET /boards/:id/card-templates` answers with — the "Create from
 * template" picker, `FR-TMPL-009` AC1 and AC4.
 *
 * `z.array(cardTemplateSchema)`, on `cardListSchema`'s precedent in `./card`
 * (`GET /lists/:id/cards` is a bare array of the element shape, ordered by
 * the server, with no envelope). No cursor and no page size: this is a
 * board's own template cards, a set `FR-TMPL-008` scopes to that board and
 * that a member curates by hand, and paging a picker would make AC4's empty
 * case ("a board has no template cards … no template option is offered")
 * indistinguishable from a page boundary.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE NAME IS NOT `cardTemplateSummarySchema`, AND THERE IS NO SUMMARY
 * SHAPE — A DELIBERATE DEPARTURE FROM THIS BLOCK'S OWN DESIGN BRIEF
 * ---------------------------------------------------------------------------
 * The brief's route table names a `cardTemplateSummarySchema[]`, which would
 * be a narrower element declared for this listing alone. Two things are wrong
 * with it. A summary would be a **second hand-maintained shape of a card**,
 * kept identical to the single read's by nobody, and the first field the
 * picker wants that the summary lacks (the card's `path`, its `cardNumber`,
 * its `position`) grows it back toward the full view one field at a time —
 * `./board-contents`' recorded "second card view" failure. And if the summary
 * were declared field-for-identical-field with `cardTemplateSchema`, it would
 * be `./copy.ts`'s own rule broken in the other direction: two names for one
 * contract, free to drift the day either route needs a field.
 *
 * ⚠ **The cost of reusing the full view is real and is not explained away:
 * `isTemplate` is `true` on every element of this array, always.** A field
 * that never varies is exactly what `packages/shared/src/events.ts` refuses
 * on an event payload — `card.created_from_template`'s own registration turns
 * on that argument — so the difference has to be stated rather than assumed.
 * A payload key is written once into an append-only table and read for ever
 * by consumers that cannot ask the source anything; this is a **response
 * field**, recomputed on every read, and its constancy here buys the property
 * `boardTemplateSchema`'s header names as the reason to extend rather than
 * nest: a caller can hand any element of this array to anything that takes a
 * `CardTemplate`, including the code that renders the badge after
 * `POST /cards/:id/template`. Two shapes would take that away to save one
 * boolean per row.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE ROUTE FILTERS, AND THE ONE THING IT DELIBERATELY DOES NOT
 * ---------------------------------------------------------------------------
 * Live, non-archived, non-deleted cards with `is_template = true` on that
 * board, ordered `position, id` (`./rank`'s rule 2 — `position` alone lets
 * the planner return ties in any order). ⚠ **`hidden_from_list` is NOT
 * filtered**: `FR-TMPL-011` AC2 says a hidden template card "is still
 * offered" in this picker, which is the whole point of hiding it. That column
 * stays otherwise unwired — `FR-TMPL-011` is Parity-Later and no backend item
 * owns it — and this listing's refusal to filter on it is the single place
 * the requirement touches shipped code.
 *
 * ⚠ **Board-scoped, and `FR-TMPL-008` AC3 is exactly this scoping**: "given a
 * template card on board A, when I look for it on board B, then it is not
 * offered there." That criterion is satisfied by the `WHERE` clause and must
 * still carry its own assertion — a scoping bug here offers another board's
 * cards with a 200 and a plausible-looking list.
 */
export const cardTemplateListSchema = z.array(cardTemplateSchema);

export type CardTemplateList = z.infer<typeof cardTemplateListSchema>;
